import React, { useState } from 'react';
import { Spin, Alert, Row, Col, Button, message } from 'antd';
import { FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import useHealthSnapshotData from '../hooks/useHealthSnapshotData';
import AdvancedHealthCalculators from './AdvancedHealthCalculators';
import HealthSystemsOverview from './HealthSystemsOverview';
import { exportMedicalRecordPDF, downloadPDF } from '../services/pdfExportService';
import { getPatientActionPlans, getActionPlanById } from '../services/actionPlanService';

// A comprehensive dashboard for patient health overview.
const ComprehensiveHealthDashboard = ({ patientId, refreshTrigger }) => {
  const [exportingPDF, setExportingPDF] = useState(false);

  const {
    loading,
    error,
    tableData,
    overallDeviation,
    systemDeviations,
    lifestyleDeviations,
    patientDemographics,
    biologicalAge,
    allParamsConfig,
    categorizedNutritionParameters
  } = useHealthSnapshotData(patientId, refreshTrigger);

  // Export PDF handler - same logic as in HealthSnapshotV2.js
  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      message.loading({ content: 'Đang tạo hồ sơ y khoa PDF...', key: 'pdf-export', duration: 0 });

      // Step 1: Collect primary action plan information (first entry, detailed view)
      const fetchPrimaryActionPlanForExport = async () => {
        if (!patientId) {
          return null;
        }

        const levelDescriptions = {
          1: 'Không ghi nhận vấn đề y khoa bất thường.',
          2: 'Có dấu hiệu cận bệnh/lâm sàng cần theo dõi sớm.',
          3: 'Đã có chẩn đoán bệnh rõ rệt, cần điều trị và theo dõi thường xuyên.'
        };

        const systemConfig = [
          {
            key: 'neurological',
            displayName: 'Thần kinh',
            aliases: ['thần kinh', 'hệ thần kinh', 'não bộ', 'neurological', 'brain', 'nervous system']
          },
          {
            key: 'cardiovascular',
            displayName: 'Tuần hoàn - Tim mạch',
            aliases: ['tuần hoàn', 'tim mạch', 'cardiovascular', 'cardiac', 'heart', 'circulatory']
          },
          {
            key: 'respiratory',
            displayName: 'Hô hấp - Phổi',
            aliases: ['hô hấp', 'phổi', 'respiratory', 'pulmonary', 'lungs', 'lung']
          },
          {
            key: 'metabolic',
            displayName: 'Chuyển hóa & Tiêu hóa',
            aliases: [
              'chuyển hóa',
              'chuyển hóa & tiêu hóa',
              'chuyển hóa và tiêu hóa',
              'tiêu hóa',
              'gan',
              'metabolic',
              'hepatic',
              'liver',
              'metabolic digestive'
            ]
          },
          {
            key: 'filtration',
            displayName: 'Thận - Tiết niệu',
            aliases: [
              'tiết niệu',
              'thận',
              'thận - tiết niệu',
              'thận/tiết niệu',
              'thận tiết niệu',
              'renal',
              'kidney',
              'lọc máu',
              'filtration',
              'urinary'
            ]
          },
          {
            key: 'musculoskeletal',
            displayName: 'Cơ xương khớp',
            aliases: ['cơ xương khớp', 'musculoskeletal', 'cơ xương', 'orthopedic', 'xương khớp']
          },
          {
            key: 'immune',
            displayName: 'Miễn dịch',
            aliases: ['miễn dịch', 'immune', 'immunology', 'hệ miễn dịch']
          }
        ];

        const parseDateToISO = (value) => {
          if (!value) return null;
          const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
          if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return null;
          }
          return date.toISOString().split('T')[0];
        };

        const extractLevelValue = (maybeLevel) => {
          if (maybeLevel === null || maybeLevel === undefined) return null;
          if (typeof maybeLevel === 'number' && !Number.isNaN(maybeLevel)) {
            return Math.min(Math.max(Math.round(maybeLevel), 1), 3);
          }
          if (typeof maybeLevel === 'string') {
            const match = maybeLevel.match(/(\d+)/);
            if (match) {
              const parsed = parseInt(match[1], 10);
              if (!Number.isNaN(parsed)) {
                return Math.min(Math.max(parsed, 1), 3);
              }
            }
            const lower = maybeLevel.toLowerCase();
            if (lower.includes('one') || lower.includes('phòng ngừa')) return 1;
            if (lower.includes('two') || lower.includes('nguy cơ')) return 2;
            if (lower.includes('three') || lower.includes('mạn tính')) return 3;
          }
          return null;
        };

        const normalizeText = (value) => {
          if (!value) return '';
          return value
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        };

        const resolveSystemKey = (rawName) => {
          if (!rawName) return null;
          const normalized = normalizeText(rawName);
          if (!normalized) return null;

          for (const config of systemConfig) {
            for (const alias of config.aliases) {
              if (normalized.includes(normalizeText(alias))) {
                return config.key;
              }
            }
          }
          return null;
        };

        const extractSystemEntryFromLine = (line) => {
          if (!line) return null;

          const sanitized = line
            .replace(/^[\-\*\u2022\d\.\)\(]+\s*/g, '')
            .replace(/\*\*/g, '')
            .replace(/__/g, '')
            .trim();

          if (!sanitized) return null;

          const lowerSanitized = normalizeText(sanitized);
          if (
            lowerSanitized.startsWith('cap do') ||
            lowerSanitized.startsWith('cấp độ') ||
            lowerSanitized.startsWith('giai thich') ||
            lowerSanitized.startsWith('giai-thich') ||
            lowerSanitized.startsWith('explanation') ||
            lowerSanitized.startsWith('care tier')
          ) {
            return null;
          }

          let namePart = null;
          let rest = null;

          const colonIndex = sanitized.indexOf(':');
          if (colonIndex !== -1) {
            namePart = sanitized.slice(0, colonIndex).trim();
            rest = sanitized.slice(colonIndex + 1).trim();
          } else {
            const dashMatch = sanitized.match(/(.+?)[\-–—]\s*(Mức|Muc|Tầng|Tang|Level|Bậc|Bac)/i);
            if (dashMatch) {
              namePart = dashMatch[1].trim();
              rest = sanitized.slice(dashMatch.index + dashMatch[1].length + 1).trim();
            }
          }

          if (!namePart || !rest) {
            return null;
          }

          // Enhanced level extraction to support "Bậc 2" format
          const levelRegex = /(Mức\s*độ|Muc\s*do|Tầng|Tang|Level|Bậc|Bac)\s*(\d)/i;
          const levelMatch = rest.match(levelRegex);
          const level = levelMatch ? extractLevelValue(levelMatch[2]) : null;

          let description = rest;
          if (levelMatch) {
            const afterLevel = rest.slice(levelMatch.index + levelMatch[0].length).trim();
            description = afterLevel.replace(/^[\-–—:]+/, '').trim();
            if (!description) {
              const beforeLevel = rest.slice(0, levelMatch.index).trim();
              description = beforeLevel;
            }
          }

          description = description?.replace(/\s+/g, ' ').trim();

          return {
            name: namePart,
            level,
            medical_issue: description
          };
        };

        const parseCareSystemsFromMarkdown = (markdown) => {
          if (!markdown || typeof markdown !== 'string') return [];

          const lines = markdown.replace(/\r/g, '\n').split('\n').map(line => line.trim()).filter(Boolean);
          const results = [];
          let current = null;

          lines.forEach((rawLine) => {
            const cleanLine = rawLine.replace(/\*\*/g, '').trim();
            if (!cleanLine) return;

            const entry = extractSystemEntryFromLine(cleanLine);
            if (entry) {
              results.push(entry);
              current = entry;
              return;
            }

            if (current) {
              const continuation = cleanLine.replace(/^[\-–—•]+/, '').trim();
              if (continuation && !normalizeText(continuation).startsWith('cap do')) {
                current.medical_issue = `${current.medical_issue ? `${current.medical_issue} ` : ''}${continuation}`.trim();
              }
            }
          });

          return results;
        };


        const finalizeCareSystems = (entries, explicitLevelOverrides = null) => {
          const resolveExplicitLevel = (key) => {
            if (!explicitLevelOverrides) return null;
            if (explicitLevelOverrides instanceof Map) {
              return explicitLevelOverrides.get(key) ?? null;
            }
            if (typeof explicitLevelOverrides === 'object') {
              return explicitLevelOverrides[key] ?? null;
            }
            return null;
          };

          const map = new Map();
          (entries || []).forEach((entry) => {
            if (!entry?.key) return;
            const existing = map.get(entry.key) || {};
            const merged = {
              key: entry.key,
              name: entry.name || existing.name,
              level: extractLevelValue(entry.level) ?? existing.level,
              medical_issue: entry.medical_issue?.toString()?.trim() || existing.medical_issue
            };
            map.set(entry.key, merged);
          });

          return systemConfig.map((config) => {
            const merged = map.get(config.key) || {};
            const overrideLevel = resolveExplicitLevel(config.key);
            const resolvedLevel = overrideLevel ?? extractLevelValue(merged.level) ?? 1;
            const medicalIssue = merged.medical_issue?.trim() || levelDescriptions[resolvedLevel] || levelDescriptions[1];
            return {
              key: config.key,
              name: merged.name || config.displayName,
              level: resolvedLevel,
              medical_issue: medicalIssue
            };
          });
        };

        const extractIssueMap = (text) => {
          if (!text || typeof text !== 'string') return {};

          const lines = text.replace(/\r/g, '\n').split('\n');
          const issueMap = {};
          let currentKey = null;

          for (const rawLine of lines) {
            const cleanLine = rawLine
              .replace(/\*\*/g, '')
              .replace(/<\/?[^>]+>/g, '')
              .replace(/^[\-\u2022•]+\s*/, '')
              .trim();

            if (!cleanLine) continue;

            // Stop at section headers (markdown ## or numbered sections)
            if (cleanLine.startsWith('##') || /^\d+\.\s+[A-Z]/.test(cleanLine)) {
              console.log('[PDF Export][Issue Map] Stopping at section header:', cleanLine);
              break;
            }

            // Stop at "---" separator
            if (cleanLine === '---') {
              console.log('[PDF Export][Issue Map] Stopping at separator');
              break;
            }

            const colonIndex = cleanLine.indexOf(':');
            if (colonIndex !== -1) {
              const potentialName = cleanLine.slice(0, colonIndex).trim();
              const key = resolveSystemKey(potentialName);
              if (key) {
                const description = cleanLine.slice(colonIndex + 1).trim();
                if (description && !normalizeText(description).startsWith('muc do')) {
                  issueMap[key] = description;
                } else {
                  issueMap[key] = '';
                }
                currentKey = key;
                continue;
              }
            }

            if (!currentKey) continue;
            const normalizedContinuation = normalizeText(cleanLine);
            if (
              normalizedContinuation.startsWith('muc do') ||
              normalizedContinuation.startsWith('cap do') ||
              normalizedContinuation.startsWith('bac ') ||      // Skip "Bậc X"
              normalizedContinuation.startsWith('level') ||
              normalizedContinuation.length === 0
            ) {
              continue;
            }

            const existing = issueMap[currentKey] || '';
            issueMap[currentKey] = `${existing} ${cleanLine}`.trim();
          }

          Object.keys(issueMap).forEach((key) => {
            issueMap[key] = issueMap[key]
              .replace(/\s+/g, ' ')
              .replace(/\s*[-–—]+\s*$/g, '')
              .trim();
          });

          return issueMap;
        };

        const parseMedicationSchedule = (content) => {
          if (!content || typeof content !== 'string') return [];

          const lines = content.replace(/\r/g, '\n').split('\n');
          const candidateLines = [];

          lines.forEach((rawLine) => {
            let text = rawLine.replace(/^\s*[-•\*\u2022]?\s*/, '').trim();
            if (!text || text === '---') return;

            if (/bổ sung thuốc/i.test(normalizeText(text)) || /thuốc đang sử dụng/i.test(normalizeText(text))) {
              const afterColon = text.split(/[:：]/).slice(1).join(':').trim();
              if (afterColon) {
                afterColon
                  .split(/[,;]/)
                  .map((entry) => entry.trim())
                  .filter(Boolean)
                  .forEach((entry) => candidateLines.push(entry));
              }
              return;
            }

            candidateLines.push(text);
          });

          const medications = [];

          candidateLines.forEach((line) => {
            let text = line.replace(/\s+/g, ' ').trim();
            if (!text) return;
            text = text.replace(/\s*\.*$/, '');

            let name = text;
            let descriptor = '';
            let purpose = '';

            const colonIndex = text.indexOf(':');
            if (colonIndex !== -1 && colonIndex < text.length - 1) {
              name = text.slice(0, colonIndex).trim();
              descriptor = text.slice(colonIndex + 1).trim();
            } else {
              const doseMatch = text.match(/^(.+?)(\d.*)$/);
              if (doseMatch) {
                name = doseMatch[1].trim();
                descriptor = doseMatch[2].trim();
              }
            }

            if (!descriptor && name !== text) {
              descriptor = text.replace(name, '').trim();
            }

            descriptor = descriptor.replace(/\s*\.*$/, '');

            if (!descriptor && name === text) {
              descriptor = '';
            }

            if (descriptor.includes('. ')) {
              const [firstSentence, ...restSentences] = descriptor.split('. ');
              descriptor = firstSentence.trim();
              purpose = restSentences.join('. ').trim();
            } else {
              const purposeMatch = descriptor.match(/;\s*(.+)$/);
              if (purposeMatch) {
                purpose = purposeMatch[1].trim();
                descriptor = descriptor.slice(0, purposeMatch.index).trim();
              }
            }

            medications.push({
              stt: medications.length + 1,
              name,
              dosage: descriptor,
              purpose
            });
          });

          const filtered = medications
            .filter((entry) => {
              const normalizedName = normalizeText(entry.name);
              if (!normalizedName) return false;
              if (normalizedName === '--') return false;
              if (normalizedName.startsWith('bo sung thuoc')) return false;
              return true;
            })
            .map((entry) => ({
              ...entry,
              name: entry.name.replace(/\s*\.*$/, '').trim(),
              dosage: entry.dosage.replace(/\s*\.*$/, '').trim(),
              purpose: entry.purpose.replace(/\s*\.*$/, '').trim()
            }));

          return filtered.map((entry, index) => ({
            ...entry,
            stt: index + 1
          }));
        };

        const convertLabTest = (raw) => {
          let text = raw.trim();
          if (!text) return null;
          text = text.replace(/\s+/g, ' ').replace(/\s*\.*$/, '');

          let name = text;
          let purpose = '';
          let notes = '';

          const purposeMatch = /(?:^|\s)(để|nhằm|giúp)\b.+$/i.exec(text);
          if (purposeMatch) {
            purpose = purposeMatch[0].trim().replace(/\s*\.*$/, '');
            name = text.slice(0, purposeMatch.index).trim().replace(/\s*[;,]$/, '');
          } else {
            const sentences = text.split('. ');
            if (sentences.length > 1) {
              name = sentences[0].trim().replace(/\s*\.*$/, '');
              purpose = sentences.slice(1).join('. ').trim().replace(/\s*\.*$/, '');
            }
          }

          const notesMatch = name.match(/\((.+?)\)/);
          if (notesMatch) {
            notes = notesMatch[1].trim();
            name = name.replace(/\((.+?)\)/, '').trim();
          }

          const normalizedName = normalizeText(name);
          if (!normalizedName || normalizedName === '--') {
            return null;
          }

          return {
            name,
            notes,
            purpose
          };
        };

        const parseLabTestGroup = (text) => {
          // New format: "test1, test2; test3; system – purpose"
          // Multiple test names separated by "," or ";"
          // Last ";" separates tests from system-purpose
          if (!text) return [];

          const normalized = text.trim();
          if (!normalized) return [];

          // Find last semicolon
          const lastSemicolonIndex = normalized.lastIndexOf(';');

          if (lastSemicolonIndex === -1) {
            // No semicolon - treat as single test with no purpose
            return [{
              name: normalized,
              notes: '',
              purpose: ''
            }];
          }

          // Split: everything before last ";" is test names, after is system-purpose
          const testNamesText = normalized.substring(0, lastSemicolonIndex).trim();
          const systemPurposeText = normalized.substring(lastSemicolonIndex + 1).trim();

          // Parse system-purpose
          let purpose = '';
          if (systemPurposeText) {
            // Split by "–" or "-" into parts
            const purposeParts = systemPurposeText.split(/\s*[–-]\s+/).map(p => p.trim()).filter(Boolean);
            purpose = purposeParts.map(p => {
              // Capitalize first letter
              return p.charAt(0).toUpperCase() + p.slice(1);
            }).join('. ');
          }

          // Split test names by ";" or "," but only outside parentheses
          const testNames = [];
          let buffer = '';
          let parenDepth = 0;

          for (let i = 0; i < testNamesText.length; i++) {
            const char = testNamesText[i];

            if (char === '(') parenDepth++;
            if (char === ')') parenDepth--;

            if ((char === ',' || char === ';') && parenDepth === 0) {
              if (buffer.trim()) testNames.push(buffer.trim());
              buffer = '';
            } else {
              buffer += char;
            }
          }
          if (buffer.trim()) testNames.push(buffer.trim());

          // Return array of test objects, all with same purpose
          return testNames.map(name => ({
            name: name,
            notes: '',
            purpose: purpose
          }));
        };

        const splitLabTests = (text) => {
          if (!text) return [];

          const normalizedText = text.replace(/\s+/g, ' ').trim();
          if (!normalizedText) return [];

          // Test groups separated by ". " (period + space)
          // Need to handle parentheses correctly (don't split inside notes)

          const testGroups = [];
          let buffer = '';
          let inParens = 0;

          for (let i = 0; i < normalizedText.length; i++) {
            const char = normalizedText[i];

            if (char === '(') inParens++;
            if (char === ')') inParens--;

            buffer += char;

            // Check if we hit a period followed by space, outside of parentheses
            if (char === '.' && inParens === 0 && i + 1 < normalizedText.length && normalizedText[i + 1] === ' ') {
              // Check if next char after space is uppercase or number (new test group starts)
              if (i + 2 < normalizedText.length && /[A-Z0-9]/.test(normalizedText[i + 2])) {
                testGroups.push(buffer.slice(0, -1).trim()); // Remove trailing period
                buffer = '';
                i++; // Skip the space
              }
            }
          }

          // Push remaining buffer
          if (buffer.trim()) {
            testGroups.push(buffer.trim());
          }

          // Parse each test group and flatten results
          const allTests = [];
          testGroups.forEach(group => {
            const tests = parseLabTestGroup(group);
            allTests.push(...tests);
          });

          return allTests.filter((entry) => entry && entry.name);
        };

        const parseLabSchedule = (content) => {
          if (!content || typeof content !== 'string') return [];

          const lines = content.replace(/\r/g, '\n').split('\n');
          const schedule = [];
          let currentEntry = null;

          lines.forEach((rawLine) => {
            let text = rawLine.replace(/^\s*[-•\*\u2022]?\s*/, '').trim();
            if (!text || text === '---') return;

            const frequencyMatch = text.match(/\*\*(.+?)\*\*/);
            if (frequencyMatch) {
              const frequency = frequencyMatch[1].replace(/[:：]/g, '').trim();
              const remainder = text.slice(frequencyMatch.index + frequencyMatch[0].length).trim().replace(/^[:：\-\u2013\u2014\s]+/, '');
              currentEntry = {
                stt: schedule.length + 1,
                frequency,
                tests: splitLabTests(remainder)
              };
              schedule.push(currentEntry);
            } else if (currentEntry) {
              currentEntry.tests.push(...splitLabTests(text));
            }
          });

          const filteredSchedule = schedule
            .map((entry) => {
              const filteredTests = entry.tests
                .filter((test) => test && normalizeText(test.name) && normalizeText(test.name) !== '--')
                .map((test) => ({
                  name: test.name.replace(/\s*\.*$/, '').trim(),
                  notes: test.notes ? test.notes.replace(/\s*\.*$/, '').trim() : '',
                  purpose: test.purpose ? test.purpose.replace(/\s*\.*$/, '').trim() : ''
                }));

              if (!filteredTests.length) {
                return null;
              }

              return {
                stt: entry.stt,
                frequency: entry.frequency.replace(/\s*\.*$/, '').trim(),
                tests: filteredTests
              };
            })
            .filter(Boolean)
            .map((entry, index) => ({
              ...entry,
              stt: index + 1
            }));

          return filteredSchedule;
        };

        const collectCareSystemEntries = (source) => {
          if (!source) return [];

          const convertItem = (key, item) => {
            const nameCandidate =
              item?.name ||
              item?.systemName ||
              item?.system ||
              item?.title ||
              (typeof key === 'string' ? key : '') ||
              '';

            const systemKey = resolveSystemKey(nameCandidate);
            if (!systemKey) {
              return null;
            }

            const systemMeta = systemConfig.find((cfg) => cfg.key === systemKey);

            const levelFromData =
              extractLevelValue(item?.level) ??
              extractLevelValue(item?.tier) ??
              extractLevelValue(item?.careLevel) ??
              extractLevelValue(item?.levelCode) ??
              extractLevelValue(item?.status) ??
              extractLevelValue(key);

            const level = levelFromData ?? 1;

            const description =
              item?.medicalIssue ||
              item?.issue ||
              item?.description ||
              item?.summary ||
              item?.recommendation ||
              levelDescriptions[level] ||
              null;

            return {
              key: systemKey,
              name: systemMeta?.displayName || nameCandidate || 'Không xác định',
              level,
              medical_issue: description || levelDescriptions[level] || ''
            };
          };

          const collectedEntries = [];

          if (Array.isArray(source)) {
            source.forEach((item, index) => {
              const converted = convertItem(item?.key ?? item?.system ?? index, item);
              if (converted) collectedEntries.push(converted);
            });
          } else if (typeof source === 'object' && source) {
            Object.entries(source).forEach(([key, value]) => {
              const converted = convertItem(key, value);
              if (converted) collectedEntries.push(converted);
            });
          } else if (typeof source === 'string') {
            parseCareSystemsFromMarkdown(source).forEach((parsed, index) => {
              const converted = convertItem(parsed?.name ?? index, parsed);
              if (converted) collectedEntries.push(converted);
            });
          }

          return collectedEntries;
        };

        const normaliseActionPlan = (plan) => {
          if (!plan) return null;

          const period = plan.planPeriod || plan.plan_period || plan.period || {};
          const start = period.startDate || period.start || period.begin || period.from || null;
          const end = period.endDate || period.end || period.finish || period.to || null;

          const sectionsArray = Array.isArray(plan?.sections) ? plan.sections : [];

          // SIMPLIFIED LOGIC: Use fixed sectionId and exact title matching
          // Debug: Show all active_problems sections
          const activeProblemsSections = sectionsArray.filter((section) => section.sectionId === 'active_problems');
          console.log('[PDF Export][Simple Logic] ===== ALL ACTIVE_PROBLEMS SECTIONS =====');
          console.log('[PDF Export][Simple Logic] Total active_problems sections:', activeProblemsSections.length);
          activeProblemsSections.forEach((section, idx) => {
            console.log(`[PDF Export][Simple Logic]   Section ${idx + 1}:`);
            console.log(`[PDF Export][Simple Logic]     - title: "${section.title}"`);
            console.log(`[PDF Export][Simple Logic]     - normalized: "${normalizeText(section.title)}"`);
            console.log(`[PDF Export][Simple Logic]     - content length: ${(section.currentContent || section.originalContent || '').length}`);
          });
          console.log('[PDF Export][Simple Logic] ===== END ALL SECTIONS =====');

          // Find level section: sectionId='active_problems', title='A. Phân loại bậc chăm sóc theo hệ cơ quan'
          const levelSection = sectionsArray.find((section) =>
            section.sectionId === 'active_problems' &&
            section.title.includes('Phân loại bậc chăm sóc theo hệ cơ quan')
          );

          // Find issue section: sectionId='active_problems', title='1. Các vấn đề bệnh lý đang hoạt động'
          const issueSection = sectionsArray.find((section) =>
            section.sectionId === 'active_problems' &&
            section.title.includes('Các vấn đề bệnh lý đang hoạt động')
          );

          console.log('[PDF Export][Simple Logic] ===== SECTION MATCHING =====');
          console.log('[PDF Export][Simple Logic] levelSection found:', levelSection?.title || 'NOT FOUND');
          console.log('[PDF Export][Simple Logic] issueSection found:', issueSection?.title || 'NOT FOUND');
          console.log('[PDF Export][Simple Logic] ===== END SECTION MATCHING =====');

          // Extract level content
          const levelSectionContent = levelSection?.currentContent || levelSection?.originalContent || '';

          // Parse levels from section content
          const levelPriorityEntries = collectCareSystemEntries(levelSectionContent);
          const explicitLevelOverrides = new Map();
          levelPriorityEntries.forEach((entry) => {
            if (!entry?.key) return;
            const explicitLevel = extractLevelValue(entry.level);
            if (explicitLevel) {
              explicitLevelOverrides.set(entry.key, explicitLevel);
            }
          });

          console.log('[PDF Export][Simple Logic] ===== LEVEL EXTRACTION =====');
          console.log('[PDF Export][Simple Logic] levelSectionContent length:', levelSectionContent.length);
          console.log('[PDF Export][Simple Logic] levelPriorityEntries:', levelPriorityEntries);
          console.log('[PDF Export][Simple Logic] explicitLevelOverrides:', Array.from(explicitLevelOverrides.entries()));
          console.log('[PDF Export][Simple Logic] ===== END LEVEL EXTRACTION =====');

          // Extract issue content
          const issueSectionContent = issueSection?.currentContent || issueSection?.originalContent || '';

          console.log('[PDF Export][Simple Logic] ===== ISSUE EXTRACTION =====');
          console.log('[PDF Export][Simple Logic] issueSectionContent length:', issueSectionContent.length);
          console.log('[PDF Export][Simple Logic] issueSectionContent (first 500 chars):\n', issueSectionContent.substring(0, 500));
          console.log('[PDF Export][Simple Logic] ===== END ISSUE EXTRACTION =====');

          // Parse issues from section content
          const issueDescriptions = extractIssueMap(issueSectionContent);

          console.log('[PDF Export][Simple Logic] ===== ISSUE MAP =====');
          console.log('[PDF Export][Simple Logic] issueDescriptions keys:', Object.keys(issueDescriptions));
          Object.entries(issueDescriptions).forEach(([key, value]) => {
            console.log(`[PDF Export][Simple Logic]   ${key}: "${value.substring(0, 100)}${value.length > 100 ? '...' : ''}"`);
          });
          console.log('[PDF Export][Simple Logic] ===== END ISSUE MAP =====');

          // Finalize care systems by mapping system names
          const careSystemsWithKeys = finalizeCareSystems(levelPriorityEntries, explicitLevelOverrides).map((system) => {
            const overrideIssue = issueDescriptions[system.key];
            return {
              ...system,
              medical_issue: overrideIssue?.trim() || system.medical_issue
            };
          });

          // Debug care_systems generation
          console.log('[PDF Export][Simple Logic] ===== CARE SYSTEMS GENERATION =====');
          console.log('[PDF Export][Simple Logic] Final care systems count:', careSystemsWithKeys.length);

          if (careSystemsWithKeys.length > 0) {
            console.log('[PDF Export][Simple Logic] Care systems details:');
            careSystemsWithKeys.forEach((system, idx) => {
              console.log(`[PDF Export][Simple Logic]   System ${idx + 1}:`, {
                name: system.name,
                key: system.key,
                level: system.level,
                level_type: typeof system.level,
                medical_issue_length: system.medical_issue?.length || 0,
                medical_issue_preview: system.medical_issue?.substring(0, 80) + (system.medical_issue?.length > 80 ? '...' : '')
              });
            });
          } else {
            console.warn('[PDF Export][Simple Logic] ⚠️  No care systems generated!');
          }
          console.log('[PDF Export][Simple Logic] ===== END CARE SYSTEMS GENERATION =====');

          const medicationSection = sectionsArray.filter((section) => section.sectionId === 'medications').pop();
          const medicationSchedule = parseMedicationSchedule(medicationSection?.currentContent || medicationSection?.originalContent || '');

          // Find lab section: sectionId='active_problems', title contains 'cận lâm sàng'
          const labsSection = sectionsArray.find((section) =>
            section.sectionId === 'active_problems' &&
            section.title.includes('Cận lâm sàng cần thực hiện')
          );
          const labSchedule = parseLabSchedule(labsSection?.currentContent || labsSection?.originalContent || '');

          const getSectionContentById = (sectionId) => {
            const section = sectionsArray.find((entry) => entry.sectionId === sectionId);
            return (
              section?.currentContent ||
              section?.originalContent ||
              ''
            );
          };

          const sanitizeSpacing = (value) =>
            typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';

          const parseBulletLines = (content) => {
            if (!content || typeof content !== 'string') return [];
            return content
              .replace(/\r/g, '\n')
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line && line !== '---')
              .map((line) => line.replace(/^[-•*]\s*/, '').trim());
          };

          const uniq = (array) => Array.from(new Set((array || []).filter(Boolean).map((item) => item.trim())));

          const extractFrequencySegments = (text) => {
            if (!text) return [];
            const patterns = [
              /(≥|≤|<)\s?[^,.;]+/gi,
              /\bít nhất[^,.;]+/gi,
              /\bmỗi[^,.;]+/gi,
              /\btrong[^,.;]+(?:ngày|tuần|tháng|năm|giờ)/gi,
              /\btrong vòng[^,.;]+/gi,
              /\bkhoảng[^,.;]+(?:ngày|tuần|tháng|năm)/gi,
              /\b\d+[^,.;]*\/(?:ngày|tuần|tháng|năm)/gi
            ];
            const segments = [];
            const seenRanges = [];
            const addSegment = (segment, start, end) => {
              if (!segment) return;
              if (seenRanges.some(([s, e]) => (s <= start && start < e) || (s < end && end <= e))) return;
              segments.push(segment.trim());
              seenRanges.push([start, end]);
            };
            patterns.forEach((pattern) => {
              let match;
              const regex = new RegExp(pattern);
              while ((match = regex.exec(text)) !== null) {
                addSegment(match[0], match.index, regex.lastIndex);
              }
            });
            return uniq(segments);
          };

          const parseImagingLine = (rawLine) => {
            const cleaned = sanitizeSpacing(rawLine);
            if (!cleaned) return null;

            const dashTokens = [' — ', ' – ', ' - '];
            let base = cleaned;
            let systemPart = '';
            for (const token of dashTokens) {
              const idx = cleaned.lastIndexOf(token);
              if (idx !== -1) {
                systemPart = cleaned.slice(idx + token.length).trim();
                base = cleaned.slice(0, idx).trim();
                break;
              }
            }

            let title = base;
            let details = '';
            const colonIndex = base.indexOf(':');
            if (colonIndex !== -1) {
              title = base.slice(0, colonIndex).trim();
              details = base.slice(colonIndex + 1).trim();
            } else {
              details = base;
            }

            const frequencySegments = extractFrequencySegments(details || base);
            let description = details;
            frequencySegments.forEach((segment) => {
              description = description.replace(segment, '');
            });
            description = sanitizeSpacing(description).replace(/^[,.;-]+/, '').replace(/[,.;-]+$/, '').trim();

            const itemText = sanitizeSpacing(title) || sanitizeSpacing(base) || cleaned;
            const normalizedDescription = sanitizeSpacing(description);
            const normalizedItem = sanitizeSpacing(itemText);
            const purposeText = normalizeText(normalizedDescription) === normalizeText(normalizedItem) ? '' : normalizedDescription;

            return {
              item: itemText,
              frequency: uniq(frequencySegments).join('; '),
              system: sanitizeSpacing(systemPart),
              purpose: purposeText
            };
          };

          const parseReferralLine = (rawLine) => {
            const cleaned = sanitizeSpacing(rawLine);
            if (!cleaned) return null;

            const dashTokens = [' — ', ' – ', ' - '];
            let itemPart = cleaned;
            let remainder = '';
            for (const token of dashTokens) {
              const idx = cleaned.indexOf(token);
              if (idx !== -1) {
                itemPart = cleaned.slice(0, idx).trim();
                remainder = cleaned.slice(idx + token.length).trim();
                break;
              }
            }

            let systemText = '';
            let description = remainder;
            const systemIdx = remainder.toLowerCase().lastIndexOf('hệ ');
            if (systemIdx !== -1) {
              systemText = remainder.slice(systemIdx).replace(/^[,–-]\s*/, '').trim();
              description = remainder.slice(0, systemIdx).replace(/[,-]\s*$/, '').trim();
            }

            return {
              item: itemPart,
              frequency: '',
              system: systemText,
              purpose: description
            };
          };

          const parseImagingProcedureLine = (line) => {
            // Format: Hạng Mục - Chuyên Khoa; Tần Suất; Hệ Cơ Quan; Mục Đích
            // Skip lines without semicolon
            if (!line.includes(';')) {
              return null;
            }

            const parts = line.split(';').map(p => p.trim());
            if (parts.length < 2) {
              return null;
            }

            const itemSpecialty = parts[0] || '';
            let frequency = '';
            let system = '';
            let purpose = '';

            // Helper function to detect if text is a frequency pattern
            const isFrequencyPattern = (text) => {
              // Check for patterns like: "1 lần/năm", "2 lần/tháng", "1/năm", "mỗi năm", etc.
              const frequencyPatterns = [
                /\d+\s*(lần|lượt)\s*\/\s*(năm|tháng|tuần|ngày)/i,  // "1 lần/năm", "2 lần/tháng"
                /\d+\s*\/\s*(năm|tháng|tuần|ngày)/i,                // "1/năm", "2/tháng"
                /mỗi\s+(năm|tháng|tuần|ngày)/i,                     // "mỗi năm", "mỗi tháng"
                /hàng\s+(năm|tháng|tuần|ngày)/i                     // "hàng năm", "hàng tháng"
              ];
              return frequencyPatterns.some(pattern => pattern.test(text));
            };

            // Check if part is frequency pattern
            for (let i = 1; i < parts.length; i++) {
              const part = parts[i];
              if (isFrequencyPattern(part) && !frequency) {
                frequency = part;
              } else if (!system) {
                system = part;
              } else if (!purpose) {
                purpose = part;
              }
            }

            return {
              item_specialty: itemSpecialty,
              frequency: frequency,
              system: system,
              purpose: purpose
            };
          };

          const buildImagingSchedule = () => {
            const imagingContent = getSectionContentById('imaging_procedures');
            const imagingLines = parseBulletLines(imagingContent);

            return imagingLines
              .map(parseImagingProcedureLine)
              .filter(entry => entry !== null)
              .slice(0, 4); // Limit to max 4 rows
          };

          const buildDiagnosticSchedule = () => {
            const imagingLines = parseBulletLines(getSectionContentById('imaging_procedures'));
            const referralLines = parseBulletLines(getSectionContentById('referrals'));

            const combinedEntries = [];

            imagingLines.forEach((line) => {
              const parsed = parseImagingLine(line);
              if (!parsed) return;
              combinedEntries.push(parsed);
            });

            referralLines.forEach((line) => {
              const parsed = parseReferralLine(line);
              if (!parsed) return;
              combinedEntries.push(parsed);
            });

          return combinedEntries.map((entry, index) => {
            const itemLines = [sanitizeSpacing(entry.item)];
            if (entry.frequency) {
              itemLines.push(`Tần suất: ${entry.frequency}`);
            }
            const itemText = itemLines.filter(Boolean).join('\n') || '—';

            const purposeParts = [];
            if (entry.system) {
              const systemText = entry.system.toLowerCase().startsWith('hệ') ? entry.system : `Hệ ${entry.system}`;
              purposeParts.push(systemText);
            }
            if (entry.purpose) {
              purposeParts.push(entry.purpose);
            }
            const purposeText = purposeParts.length ? purposeParts.join(' – ') : '—';

            return {
              stt: index + 1,
              diagnostic_info: itemText,
              purpose_info: purposeText
            };
          }).filter((entry) => entry.diagnostic_info || entry.purpose_info);
          };

          const buildSmartGoalSchedule = () => {
            const smartLines = parseBulletLines(getSectionContentById('smart_goals'));

            return smartLines.map((line, index) => {
              const cleaned = sanitizeSpacing(line);
              if (!cleaned) {
                return {
                  stt: index + 1,
                  goal_time: '—',
                  systems: [],
                  activities: '—'
                };
              }

              // Split by " – " (with spaces) to get 3 parts
              const parts = cleaned.split(/\s–\s/);

              if (parts.length < 3) {
                // Fallback if format doesn't match
                return {
                  stt: index + 1,
                  goal_time: cleaned,
                  systems: [],
                  activities: '—'
                };
              }

              // Part 1: Goal - Time
              const goalTime = parts[0].trim();

              // Part 2: Systems (split by "–" without spaces)
              const systemsText = parts[1].trim();
              const systems = systemsText.split(/–/).map(s => s.trim()).filter(Boolean);

              // Part 3: Activities
              const activities = parts.slice(2).join(' – ').trim();

              return {
                stt: index + 1,
                goal_time: goalTime || '—',
                systems: systems,
                activities: activities || '—'
              };
            });
          };

          const imagingSchedule = buildImagingSchedule();
          const diagnosticSchedule = buildDiagnosticSchedule();
          const smartGoalsSchedule = buildSmartGoalSchedule();
          const vaccinationSchedule = parseBulletLines(getSectionContentById('vaccinations')).map((line, index) => ({
            stt: index + 1,
            vaccination_info: sanitizeSpacing(line) || '—'
          }));

          console.log('[PDF Export DEBUG] Imaging schedule entries:', imagingSchedule);
          console.log('[PDF Export DEBUG] Diagnostic schedule entries:', diagnosticSchedule);
          console.log('[PDF Export DEBUG] SMART goals entries:', smartGoalsSchedule);
          console.log('[PDF Export DEBUG] Vaccination schedule entries:', vaccinationSchedule);

          const careSystemsForPDF = careSystemsWithKeys.map(({ key, ...rest }) => rest);

          console.log('[PDF Export][Final Data] ===== HEALTHCARE PLAN DATA FOR PDF =====');
          console.log('[PDF Export][Final Data] created_by:', plan.createdBy?.name || plan.createdBy?.fullName || plan.createdBy);
          console.log('[PDF Export][Final Data] created_at:', parseDateToISO(plan.createdAt));
          console.log('[PDF Export][Final Data] plan_period:', { start: parseDateToISO(start), end: parseDateToISO(end) });
          console.log('[PDF Export][Final Data] care_systems count:', careSystemsForPDF.length);
          console.log('[PDF Export][Final Data] medication_schedule count:', medicationSchedule.length);
          console.log('[PDF Export][Final Data] lab_schedule count:', labSchedule.length);

          if (careSystemsForPDF.length > 0) {
            console.log('[PDF Export][Final Data] care_systems being sent to PDF:');
            careSystemsForPDF.forEach((system, idx) => {
              console.log(`[PDF Export][Final Data]   ${idx + 1}. ${system.name} - Level ${system.level}`);
            });
          } else {
            console.warn('[PDF Export][Final Data] ⚠️  care_systems array is EMPTY - PDF will have no system data!');
          }
          console.log('[PDF Export][Final Data] ===== END HEALTHCARE PLAN DATA =====');

          return {
            created_by:
              plan.createdBy?.name ||
              plan.createdBy?.fullName ||
              plan.createdBy?.displayName ||
              plan.createdBy ||
              'Unknown',
            created_at: parseDateToISO(plan.createdAt) || null,
            plan_period: {
              start: parseDateToISO(start),
              end: parseDateToISO(end)
            },
            care_systems: careSystemsForPDF,
            medication_schedule: medicationSchedule,
            lab_schedule: labSchedule,
            imaging_schedule: imagingSchedule,
            diagnostic_schedule: diagnosticSchedule,
            smart_goals: smartGoalsSchedule,
            vaccination_schedule: vaccinationSchedule
          };
        };

        try {
          const listResponse = await getPatientActionPlans(patientId, {
            limit: 1,
            skip: 0,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          });

          const firstPlan =
            listResponse?.data?.actionPlans?.[0] ||
            listResponse?.data?.plans?.[0] ||
            listResponse?.actionPlans?.[0] ||
            null;

          if (!firstPlan) {
            console.log('[PDF Export][Action Plan] No action plans found for patient:', patientId);
            return null;
          }

          console.log('[PDF Export][Action Plan] First plan from list:', firstPlan);

          let detailedPlan = firstPlan;

          if (!firstPlan.sections || !firstPlan.careSystems) {
            try {
              const detailResponse = await getActionPlanById(firstPlan._id || firstPlan.id, false);
              detailedPlan =
                detailResponse?.data?.actionPlan ||
                detailResponse?.data ||
                detailResponse?.actionPlan ||
                firstPlan;
              console.log('[PDF Export][Action Plan] Loaded detailed plan:', detailedPlan);
            } catch (detailError) {
              console.warn('[PDF Export][Action Plan] Could not fetch detailed plan data:', detailError);
            }
          }

          const normalisedPlan = normaliseActionPlan(detailedPlan);
          console.log('[PDF Export][Action Plan] Normalised export payload:', normalisedPlan);
          return normalisedPlan;
        } catch (actionPlanError) {
          console.error('[PDF Export][Action Plan] Failed to gather action plan data:', actionPlanError);
          return null;
        }
      };

      const actionPlanExportData = await fetchPrimaryActionPlanForExport();

      // Calculate health summary statistics from tableData (same logic as PatientHealthOverview)
      let totalParameters = 0;
      let parametersWithData = 0;
      let allClear = 0; // Tình Trạng Tốt (Good)
      let lowPriority = 0; // Tình Trạng Khá (Fair)
      let moderatePriority = 0; // Cần Lưu Ý (Warning)
      let highPriority = 0; // Theo Dõi Sát (Alert)

      tableData.forEach(categoryGroup => {
        (categoryGroup.parameters || []).forEach(param => {
          totalParameters++;

          // Count parameters with actual patient data
          if (param.currentValue !== null && param.currentValue !== undefined) {
            parametersWithData++;

            const weightedAbsZ = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;

            if (weightedAbsZ > 2.0) {
              highPriority++;
            } else if (weightedAbsZ > 1.0) {
              moderatePriority++;
            } else if (weightedAbsZ > 0.5) {
              lowPriority++;
            } else { // weightedAbsZ <= 0.5
              allClear++;
            }
          }
        });
      });

      // Calculate PREVENT score (same logic as PreventScore component)
      const getParameterValue = (paramKey) => {
        if (!tableData || !Array.isArray(tableData)) return null;
        for (const categoryGroup of tableData) {
          const param = (categoryGroup.parameters || []).find(p => p.key === paramKey);
          if (param && param.currentValue !== null && param.currentValue !== undefined) {
            const numValue = parseFloat(param.currentValue);
            return isNaN(numValue) ? null : numValue;
          }
        }
        return null;
      };

      const getRiskCategory = (riskPercent) => {
        if (riskPercent < 5) return 'Nguy Cơ Thấp';
        if (riskPercent < 7.5) return 'Nguy Cơ Trung Bình';
        if (riskPercent < 20) return 'Nguy Cơ Cao';
        return 'Nguy Cơ Rất Cao';
      };

      let preventScoreData = null;

      try {
        // Import calculatePREVENTScore dynamically
        const { calculatePREVENTScore } = await import('../utils/riskCalculators');

        const currentAge = patientDemographics?.birthDate
          ? Math.floor((new Date() - new Date(patientDemographics.birthDate)) / (365.25 * 24 * 60 * 60 * 1000))
          : patientDemographics?.age || null;

        const height = getParameterValue('vitals_height');
        const weight = getParameterValue('vitals_weight');
        const bmi = (height && weight) ? weight / Math.pow(height / 100, 2) : null;

        const riskParams = {
          age: currentAge,
          sex: patientDemographics?.sex?.toLowerCase() || null,
          totalCholesterol: getParameterValue('labs_lipid_chol_total'),
          hdlCholesterol: getParameterValue('labs_lipid_hdl'),
          systolicBP: getParameterValue('vitals_sbp'),
          bmi: bmi,
          eGFR: getParameterValue('labs_renal_egfr'),
          onBPMeds: patientDemographics?.isOnBpMeds,
          diabetes: patientDemographics?.hasDiabetes || false,
          smoker: patientDemographics?.isSmoker,
        };

        const preventResult = calculatePREVENTScore(riskParams);

        if (!preventResult.error) {
          preventScoreData = {
            totalCVD: {
              risk: parseFloat(preventResult.score),
              assessment: preventResult.category || getRiskCategory(parseFloat(preventResult.score))
            },
            ascvd: {
              risk: parseFloat(preventResult.ascvdRisk),
              assessment: getRiskCategory(parseFloat(preventResult.ascvdRisk))
            },
            heartFailure: {
              risk: parseFloat(preventResult.heartFailureRisk),
              assessment: getRiskCategory(parseFloat(preventResult.heartFailureRisk))
            },
            stroke: {
              risk: parseFloat(preventResult.strokeRisk),
              assessment: getRiskCategory(parseFloat(preventResult.strokeRisk))
            }
          };
          console.log('[PDF Export] PREVENT Score calculated:', preventScoreData);
        }
      } catch (preventError) {
        console.warn('[PDF Export] Could not calculate PREVENT score:', preventError);
      }

      // Prepare system and lifestyle data for PDF
      const systemsData = {};
      const lifestyleData = {};

      // Helper functions to get colors (matching HealthSystemsOverview.js logic)
      const getScoreColor = (score) => {
        if (score < 70) return '#D20000'; // Red
        if (score < 85) return '#F37D42'; // Orange
        return '#28A049'; // Green
      };

      const getRiskScoreColor = (riskScore) => {
        if (riskScore < 15) return '#28A049'; // Low risk is green
        if (riskScore < 30) return '#F37D42'; // Moderate risk is orange
        return '#D20000'; // High risk is red
      };

      // Extract health scores from systemDeviations with colors
      if (systemDeviations) {
        Object.keys(systemDeviations).forEach(systemKey => {
          const system = systemDeviations[systemKey];
          const structureScore = system.structure?.healthScore || 0;
          const functionScore = system.function?.healthScore || 0;
          const riskScore = system.risk?.healthScore || 0;

          // Calculate overall score (same logic as HealthSystemsOverview.js)
          const riskHealthEquivalent = Math.max(0, 100 - riskScore);
          const overallScore = (structureScore + functionScore + riskHealthEquivalent) / 3;

          // Extract top 7 contributing factors for this system
          // Contributing factors are sorted by priorityScore in the scoringEngineV2 calculation
          const contributingParams = system.structure?.contributingParams || [];

          // Filter by averageSystemWeight >= 25 (matching HealthSystemsOverview.js UI logic)
          // This ensures we only show parameters that are significantly relevant to this system
          const filteredParams = contributingParams.filter(param => param.averageSystemWeight >= 25);

          const top7ContributingFactors = filteredParams.slice(0, 7).map(param => ({
            key: param.key,
            name: param.parameterName || param.displayName || param.key,
            currentValue: param.currentValue,
            idealTarget: param.idealTarget,
            unit: param.unit || '',
            zScore: param.zScore || 0,
            priorityScore: param.priorityScore || 0,
            priority: param.priority || 'Unknown',
            averageSystemWeight: param.averageSystemWeight || 0,
            category: param.category || '',
            subcategory: param.subcategory || ''
          }));

          systemsData[systemKey] = {
            structure: {
              score: structureScore,
              color: getScoreColor(structureScore)
            },
            function: {
              score: functionScore,
              color: getScoreColor(functionScore)
            },
            risk: {
              score: riskScore,
              color: getRiskScoreColor(riskScore)
            },
            overall: {
              score: overallScore,
              color: getScoreColor(overallScore)
            },
            contributingFactors: top7ContributingFactors
          };
        });
      }

      // Extract health scores from lifestyleDeviations
      if (lifestyleDeviations) {
        Object.keys(lifestyleDeviations).forEach(domainKey => {
          const domain = lifestyleDeviations[domainKey];
          lifestyleData[domainKey] = {
            healthScore: domain.healthScore || 0
          };
        });
      }

      console.log('[PDF Export] Systems data:', systemsData);
      console.log('[PDF Export] Lifestyle data:', lifestyleData);

      // Extract top 14 high priority parameters (sorted by weighted absolute Z-score)
      const allParametersWithData = [];
      tableData.forEach(categoryGroup => {
        (categoryGroup.parameters || []).forEach(param => {
          if (param.currentValue !== null && param.currentValue !== undefined) {
            const weightedAbsZ = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;

            // Extract parameter info for PDF - only include defined fields
            const paramInfo = {
              key: param.key,
              name: param.parameterName || param.displayName || param.key,
              subcategory: param.subcategory || categoryGroup.subcategory || categoryGroup.categoryName || 'General',
              currentValue: param.currentValue,
              unit: param.unit || '',
              zScore: typeof param.zScore === 'number' ? param.zScore : 0,
              weightedAbsoluteZScore: weightedAbsZ
            };

            // Only add optional fields if they are defined (not null or undefined)
            if (param.normalRange !== null && param.normalRange !== undefined) {
              paramInfo.normalRange = param.normalRange;
            }
            if (param.targetValue !== null && param.targetValue !== undefined) {
              paramInfo.targetValue = param.targetValue;
            }
            if (param.lowerBound !== null && param.lowerBound !== undefined) {
              paramInfo.lowerBound = param.lowerBound;
            }
            if (param.upperBound !== null && param.upperBound !== undefined) {
              paramInfo.upperBound = param.upperBound;
            }

            allParametersWithData.push(paramInfo);
          }
        });
      });

      // Sort by weighted absolute Z-score (highest priority first) and take top 14
      const topPriorityParameters = allParametersWithData
        .sort((a, b) => b.weightedAbsoluteZScore - a.weightedAbsoluteZScore)
        .slice(0, 14);

      console.log('[PDF Export] Top 14 priority parameters:', topPriorityParameters);

      // Debug: Log all data being sent to backend
      const pdfExportData = {
        overallDeviation,
        healthSummary: {
          total: parametersWithData,
          good: allClear,
          fair: lowPriority,
          warning: moderatePriority,
          alert: highPriority
        },
        preventScore: preventScoreData,
        systemsData,
        lifestyleData,
        topPriorityParameters,
        biologicalAge: biologicalAge || null,
        chronologicalAge: patientDemographics?.age || null
      };

      if (actionPlanExportData) {
        pdfExportData.healthcarePlan = actionPlanExportData;
      }

      console.log('[PDF Export DEBUG] Full data being sent to backend:');
      console.log('[PDF Export DEBUG] - overallDeviation:', overallDeviation);
      console.log('[PDF Export DEBUG] - systemsData:', JSON.stringify(systemsData, null, 2));
      console.log('[PDF Export DEBUG] - lifestyleData:', JSON.stringify(lifestyleData, null, 2));
      console.log('[PDF Export DEBUG] - topPriorityParameters count:', topPriorityParameters.length);
      console.log('[PDF Export DEBUG] - biologicalAge:', biologicalAge);
      console.log('[PDF Export DEBUG] - chronologicalAge:', patientDemographics?.age);
      if (actionPlanExportData) {
        console.log('[PDF Export DEBUG] - healthcarePlan.medication_schedule length:', actionPlanExportData.medication_schedule?.length || 0);
        console.log('[PDF Export DEBUG] - healthcarePlan.lab_schedule length:', actionPlanExportData.lab_schedule?.length || 0);
        console.log('[PDF Export DEBUG] - healthcarePlan:', JSON.stringify(actionPlanExportData, null, 2));
      }

      const { blob, filename } = await exportMedicalRecordPDF(patientId, pdfExportData);

      downloadPDF(blob, filename);

      message.success({ content: 'Xuất hồ sơ y khoa thành công!', key: 'pdf-export', duration: 3 });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error({
        content: `Không thể xuất hồ sơ: ${error.response?.data?.message || error.message}`,
        key: 'pdf-export',
        duration: 5
      });
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 150px)' }}><Spin tip="Loading Comprehensive Health Dashboard..." size="large" /></div>;
  }

  if (error) {
    return <Alert message="Error Loading Comprehensive Health Data" description={error} type="error" showIcon style={{ margin: 20 }} />;
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Export Medical Record Button - Prominent placement at the top */}
      <div style={{
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '16px',
        backgroundColor: '#f0f2f5',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Button
          type="primary"
          size="large"
          onClick={handleExportPDF}
          icon={exportingPDF ? <LoadingOutlined /> : <FilePdfOutlined />}
          loading={exportingPDF}
          style={{
            fontSize: '16px',
            height: '48px',
            padding: '0 32px',
            fontWeight: 'bold'
          }}
        >
          Xuất hồ sơ y khoa
        </Button>
      </div>

      {/* Section 1: Advanced Health Calculators */}
      <AdvancedHealthCalculators
        patientId={patientId}
        loading={loading}
        tableData={tableData}
        overallDeviation={overallDeviation}
        patientDemographics={patientDemographics}
        biologicalAge={biologicalAge}
      />

      {/* Section 2: Interactive Coxcomb Charts */}
      {allParamsConfig && (
        <HealthSystemsOverview
          systemDeviations={systemDeviations}
          lifestyleDeviations={lifestyleDeviations}
          tableData={tableData}
          allParamsConfig={allParamsConfig}
          categorizedNutritionParameters={categorizedNutritionParameters}
        />
      )}
    </div>
  );
};

export default ComprehensiveHealthDashboard;
